import { Problem } from './types';

// Costo "infinito" para rutas prohibidas: lo bastante grande para que el
// algoritmo nunca las use, pero finito para no romper la aritmética.
export const BIG = 1_000_000;

export interface EffectiveProblem {
  // Problema que realmente resuelve el algoritmo (costos ya transformados).
  solveProblem: Problem;
  // Costos originales capturados por el usuario (para reportar utilidad/costo real).
  originalCosts: number[][];
  maximize: boolean;
  note: string | null;
}

// Construye la matriz de costos efectiva a partir del problema original:
//  - Maximización: se convierte a una matriz de penalización  r = maxValor − valor
//    (el problema de maximizar utilidad equivale a minimizar la penalización).
//  - Rutas prohibidas: se les asigna un costo enorme (BIG) para descartarlas.
export function buildEffective(
  original: Problem,
  maximize: boolean,
  forbidden?: boolean[][]
): EffectiveProblem {
  const m = original.supply.length;
  const n = original.demand.length;
  const originalCosts = original.costs.map((r) => r.slice());

  let maxVal = -Infinity;
  if (maximize) {
    for (let i = 0; i < m; i++)
      for (let j = 0; j < n; j++)
        if (!forbidden?.[i]?.[j]) maxVal = Math.max(maxVal, originalCosts[i][j]);
  }

  const costs: number[][] = [];
  for (let i = 0; i < m; i++) {
    costs.push([]);
    for (let j = 0; j < n; j++) {
      if (forbidden?.[i]?.[j]) {
        costs[i].push(BIG);
      } else if (maximize) {
        costs[i].push(maxVal - originalCosts[i][j]);
      } else {
        costs[i].push(originalCosts[i][j]);
      }
    }
  }

  const note = maximize
    ? `Maximización: se resuelve sobre la matriz de penalización (máx ${maxVal} − valor). La utilidad se calcula con los valores originales.`
    : null;

  return {
    solveProblem: {
      ...original,
      costs,
      forbidden,
    },
    originalCosts,
    maximize,
    note,
  };
}

// Calcula el costo/utilidad real de una asignación usando los costos originales.
export function realObjective(
  alloc: (number | null)[][],
  originalCosts: number[][]
): number {
  let sum = 0;
  for (let i = 0; i < alloc.length; i++)
    for (let j = 0; j < alloc[i].length; j++) {
      const a = alloc[i][j];
      if (a !== null) sum += a * originalCosts[i][j];
    }
  return sum;
}
